import { CompanyModel } from '@one/models/company.model';
import { SimplifiedFileModel } from '@one/models/file.model';

/** Marca nas prévias (og:site_name). A Apple pede a marca aqui, e não no og:title (TN3156). */
export const COMPANY_PREVIEW_SITE_NAME = 'AgendaJá';
/** Usado quando a empresa não preencheu a descrição de SEO. É o texto que o SeoService já usava. */
export const COMPANY_PREVIEW_FALLBACK_DESCRIPTION = 'Acesse minha agenda online e marque seu horário :)';
/** Imagem padrão: JPEG 1200×600, 91 KB (conferida em 31/10/2026). */
export const COMPANY_PREVIEW_FALLBACK_IMAGE = 'https://assets.agendaja.app/site-empresas/seo-thumb.jpg';
export const COMPANY_PREVIEW_DESCRIPTION_MAX = 300;

export type TCompanyPreviewSource = Partial<Pick<CompanyModel, 'name' | 'slug' | 'seo' | 'logo' | 'images'>>;

export interface ICompanyPreview {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  url: string;
}

/** Prévia de link de uma empresa. Função pura: dá o mesmo resultado no servidor e no navegador. */
export function buildCompanyPreview(company: TCompanyPreviewSource | null | undefined, origin: string): ICompanyPreview | null {
  const name: string = cleanText(company?.name);
  const slug: string = (company?.slug || '').trim();
  if (!name || !slug) return null;

  const image: { url: string; alt: string } = pickImage(company, name);

  return {
    title: name,
    description: truncate(cleanText(company?.seo?.description)) || COMPANY_PREVIEW_FALLBACK_DESCRIPTION,
    image: image.url,
    imageAlt: image.alt,
    url: `${origin.replace(/\/+$/, '')}/${encodeURIComponent(slug)}`
  };
}

/** Ordem: imagem de SEO (escolha explícita) > logo > primeira foto > imagem padrão. */
function pickImage(company: TCompanyPreviewSource | null | undefined, name: string): { url: string; alt: string } {
  const candidates: Array<{ file: SimplifiedFileModel | null | undefined; alt: string }> = [
    { file: company?.seo?.image, alt: name },
    { file: company?.logo, alt: `Logo de ${name}` },
    { file: company?.images?.[0], alt: `Foto de ${name}` }
  ];

  for (const candidate of candidates) {
    const url: string = httpsUrl(candidate.file?.url);
    if (url) return { url, alt: cleanText(candidate.file?.altText) || candidate.alt };
  }

  return { url: COMPANY_PREVIEW_FALLBACK_IMAGE, alt: COMPANY_PREVIEW_SITE_NAME };
}

function cleanText(value: string | null | undefined): string {
  return (value || '').replace(/\s+/g, ' ').trim();
}

function truncate(value: string): string {
  return value.length > COMPANY_PREVIEW_DESCRIPTION_MAX ? `${value.slice(0, COMPANY_PREVIEW_DESCRIPTION_MAX - 1).trimEnd()}…` : value;
}

/** Só https absoluta ("//host/x" vira "https://host/x"). http, data: e caminho relativo ficam de fora. */
function httpsUrl(value: string | null | undefined): string {
  const url: string = (value || '').trim();
  if (url.startsWith('//')) return `https:${url}`;
  return /^https:\/\/[^\s"'<>]+$/i.test(url) ? url : '';
}
